import { IconButton } from '@dixa/lib-shared-habla';
import { detect } from 'detect-browser';
import { useCallback, useEffect, useMemo, useState } from 'react';
import RecordRTC from 'recordrtc';

import {
  Sound,
  SoundMeter,
} from 'components/conversation/components/soundMeter/soundMeter';

import { AudioPlayer } from '../audioPlayer/audioPlayer';

import styles from './recordPlayer.module.scss';

type Stream = {
  file: File | null;
  recorder: RecordRTC | null;
  mediaStream: MediaStream | null;
};

type Props = {
  name: string;
  onChange: (file: null | File) => void;
};

export const RecordPlayer = ({ onChange, name }: Props): JSX.Element => {
  const [blobObjectURL, setBlobObjectURL] = useState<string | null>(null);
  const [sound, setSound] = useState<Sound[]>([]);
  const [isRecording, setIsRecording] = useState(false);
  const stream: Stream = useMemo(
    () => ({ file: null, recorder: null, mediaStream: null }),
    [],
  );

  useEffect(() => void onChange(stream.file), [onChange, stream.file]);

  const onRecordClick = useCallback(
    () =>
      setIsRecording((isRecording) => {
        const { recorder, mediaStream } = stream;

        if (isRecording && recorder && mediaStream) {
          recorder.stopRecording(() => {
            const tracks = mediaStream.getAudioTracks();
            const blob = recorder.getBlob();
            const blobObjectURL = URL.createObjectURL(blob);

            setBlobObjectURL(blobObjectURL);

            tracks.forEach((track) => track.stop());
            stream.file = new File([blob], name, { type: blob.type });
          });
        }

        if (!isRecording) {
          navigator.mediaDevices
            .getUserMedia({ audio: true })
            .then(async (mediaStream) => {
              const BrowserAudioContext =
                Reflect.get(window, 'AudioContext') ||
                Reflect.get(window, 'webkitAudioContext');

              const context: AudioContext = new BrowserAudioContext();

              const analyser = context.createAnalyser();
              const input = context.createMediaStreamSource(mediaStream);

              input.connect(analyser);
              analyser.smoothingTimeConstant = 0.2;
              analyser.fftSize = 64;

              const spectrum = new Uint8Array(analyser.fftSize);

              const browser = detect();
              const isChromium = ['chrome', 'edge-chromium'].includes(
                browser?.name || '',
              );

              const recorder = new RecordRTC(mediaStream, {
                type: 'audio',
                disableLogs: true,
                mimeType: 'audio/wav',
                recorderType: isChromium
                  ? RecordRTC.StereoAudioRecorder
                  : RecordRTC.MediaStreamRecorder,
                /*
                 * Record in chunks of 500ms.
                 *
                 * This is so that our 'ondataavailable'
                 * handler would get called every so often.
                 */
                timeSlice: 500,
                ondataavailable: () => {
                  analyser.getByteFrequencyData(spectrum);

                  const rms = Math.sqrt(
                    spectrum.reduce((acc, val) => acc + val ** 2, 0) /
                      spectrum.length,
                  );

                  setSound((sound) => [
                    ...sound,
                    {
                      output: 0,
                      /*
                       * If input falls down to 0 then we won't see a bar anymore,
                       * so we use minimum of 0.2 to still show a bar even when silent.
                       */
                      input: Math.max(rms / 100, 0.2),
                      isRecording: true,
                    },
                  ]);
                },
              });

              recorder.startRecording();

              stream.mediaStream = mediaStream;
              stream.recorder = recorder;
            });
        }

        return !isRecording;
      }),
    [name, setIsRecording, stream],
  );

  const onDeleteClick = useCallback(() => {
    stream.mediaStream = null;
    stream.recorder = null;
    stream.file = null;

    setSound([]);
    setBlobObjectURL(null);
  }, [stream, setSound, setBlobObjectURL]);

  return blobObjectURL ? (
    <>
      <AudioPlayer
        isDownloadable
        src={blobObjectURL}
        filename={name}
        onDelete={onDeleteClick}
      />
    </>
  ) : (
    <div className={styles.record}>
      <IconButton
        variant="secondary"
        icon={isRecording ? 'stop' : 'record'}
        onClick={onRecordClick}
      />
      <div className={styles.recordStatus}>
        {!isRecording ? 'Start recording' : <SoundMeter sounds={sound} />}
      </div>
      <IconButton variant="secondary" icon="trash" disabled />
    </div>
  );
};
